/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 * Licensed under the VIJAYA 1.0 License.
 *
 * NotificationContext — shared React context for real-time in-app notifications.
 * (ARCEE-ENH-008 — Real-Time Infrastructure)
 *
 * Manages:
 *  - A single WebSocket connection per user session (via ArceeRealtimeClient)
 *  - unreadCount  — live badge count for DashboardHeader bell
 *  - recentNotifications — last 10, for the header dropdown panel
 *  - latestToast  — triggers NotificationToast to show a popup
 *  - markRead()   — mark one notification read (REST + local state)
 *  - markAllRead() — mark all read (REST + local state)
 *  - refreshCount() — re-fetch count from REST (used on reconnect / manual refresh)
 *
 * Session-aware:
 *  - Listens for 'arcee:auth-change' window events dispatched by SignInPage
 *    (on login) and DashboardHeader (on logout). Re-initialises the WebSocket
 *    connection and seeds state fresh on every session change.
 *  - Discards WS messages whose application_id doesn't match envConfig.applicationId
 *    to prevent cross-app notification contamination.
 *
 * Used by:
 *  - DashboardHeader (bell badge + dropdown)
 *  - NotificationToast (auto-dismiss popup)
 *  - NotificationPreferencesPage (live inbox)
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import ArceeRealtimeClient from '../utils/realtimeClient';
import APIHelper from '../api/APIHelper';
import envConfig from '../config/envConfig';
import { retrieve } from '../utils/storage_helper';

// ── Context default ──────────────────────────────────────────────────────────
const NotificationContext = createContext({
  unreadCount: 0,
  recentNotifications: [],
  latestToast: null,
  markRead: () => {},
  markAllRead: () => {},
  refreshCount: () => {},
  fetchRecent: () => {},
});

// ── Shared APIHelper instance ─────────────────────────────────────────────────
const apiHelper = new APIHelper({
  apiUrl: envConfig.backendApiUrl,
  tokenUrl: envConfig.backendTokenUrl,
  clientId: envConfig.clientKey,
  clientSecret: envConfig.clientSecret,
});

// ── Provider ──────────────────────────────────────────────────────────────────
export function NotificationProvider({ children }) {
  const [unreadCount, setUnreadCount]                 = useState(0);
  const [recentNotifications, setRecentNotifications] = useState([]);
  const [latestToast, setLatestToast]                 = useState(null);
  // sessionKey increments on every login/logout event so the WS useEffect
  // tears down the old connection and opens a fresh one for the new session.
  const [sessionKey, setSessionKey]                   = useState(0);
  const wsClientRef = useRef(null);

  const applicationId  = envConfig.applicationId;
  const organizationId = envConfig.organizationId;

  // ── Listen for login / logout events ─────────────────────────────────
  // SignInPage dispatches 'arcee:auth-change' on successful login.
  // DashboardHeader dispatches it just before navigating to /sign-in.
  useEffect(() => {
    const handleAuthChange = () => {
      // Reset notification state immediately so stale data never bleeds
      // into the next session's view.
      setUnreadCount(0);
      setRecentNotifications([]);
      setLatestToast(null);
      setSessionKey(k => k + 1);   // triggers WS reconnect effect below
    };
    window.addEventListener('arcee:auth-change', handleAuthChange);
    return () => window.removeEventListener('arcee:auth-change', handleAuthChange);
  }, []);

  // ── Fetch unread count from REST ──────────────────────────────────────
  const refreshCount = useCallback(async () => {
    if (!applicationId) return;
    try {
      const res = await apiHelper.apiCall('/arcee/notifications/unread-count/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ application_id: applicationId }),
      });
      if (res.ok) {
        const data = await res.json();
        setUnreadCount(data.unread_count ?? 0);
      }
    } catch (_) {
      // Silent fail — WebSocket compensates for live updates
    }
  }, [applicationId]);

  // ── Fetch recent notifications for the dropdown ───────────────────────
  const fetchRecent = useCallback(async () => {
    if (!applicationId) return;
    try {
      const res = await apiHelper.apiCall('/arcee/notifications/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ application_id: applicationId, page_size: 10 }),
      });
      if (res.ok) {
        const data = await res.json();
        setRecentNotifications(data.results || []);
      }
    } catch (_) {}
  }, [applicationId]);

  // ── Mark single notification as read ─────────────────────────────────
  const markRead = useCallback(async (notificationId) => {
    try {
      await apiHelper.apiCall(`/arcee/notifications/${notificationId}/read/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        // Send application_id so the backend can scope the lookup to this app,
        // preventing cross-app mark-read when the same user is in multiple apps.
        body: JSON.stringify({ application_id: applicationId }),
      });
      setRecentNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (_) {}
  }, [applicationId]);

  // ── Mark all as read ──────────────────────────────────────────────────
  const markAllRead = useCallback(async () => {
    if (!applicationId) return;
    try {
      await apiHelper.apiCall('/arcee/notifications/mark-all-read/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ application_id: applicationId }),
      });
      setUnreadCount(0);
      setRecentNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (_) {}
  }, [applicationId]);

  // ── WebSocket setup ───────────────────────────────────────────────────
  // Runs on mount and every time sessionKey changes (login / logout).
  useEffect(() => {
    // Only connect if user is logged in and config is present
    const userData = retrieve('userData');
    if (!userData || !envConfig.wsUrl || !organizationId || !applicationId) return;

    const client = new ArceeRealtimeClient(
      organizationId,
      applicationId,
      envConfig.wsUrl,
    );

    // Handle incoming personal notification push from WebSocketDeliverer.
    // Guard: discard messages whose application_id doesn't match this deployment.
    // This prevents cross-app contamination when the same user is logged in to
    // multiple app deployments that share a backend (same session cookie).
    const unsubscribe = client.subscribe('notification', (data) => {
      const msgAppId = data.application_id != null
        ? String(data.application_id)
        : null;
      const thisAppId = applicationId != null ? String(applicationId) : null;

      if (msgAppId !== null && thisAppId !== null && msgAppId !== thisAppId) {
        // Notification is for a different application — discard silently.
        return;
      }

      // Increment bell badge
      setUnreadCount(prev => prev + 1);

      // Prepend to recent list (keep last 10)
      setRecentNotifications(prev => [
        {
          id: data.id,
          title: data.title,
          body: data.body,
          priority: data.priority,
          event_type: data.event_type,
          created_at: data.created_at,
          is_read: false,
        },
        ...prev.slice(0, 9),
      ]);

      // Trigger toast — unique timestamp forces the effect to re-fire
      // even if two notifications arrive with the same content
      setLatestToast({ ...data, _ts: Date.now() });
    });

    client.connect();
    wsClientRef.current = client;

    // ── Seeding from REST on mount: BOTH CALLS ARE OFF ───────────────────────
    //
    // DISABLED ON REQUEST — notifications are not needed for now. Re-enable by
    // uncommenting the two lines below; nothing else has to change.
    //
    // This provider wraps the WHOLE app, so these two ran on every route a
    // signed-in person opened: the sign-in screen, every public event page, and
    // the organizer surface, none of which render a bell at all.
    //
    // refreshCount() drew the unread badge. With it off the badge simply does
    // not appear until a live notification arrives over the WebSocket below,
    // which still increments it.
    //
    // fetchRecent() filled the dropdown list. DashboardHeader now calls it when
    // the dropdown actually opens, so the bell still works on demand and the
    // notifications page keeps its own fetches — the feature stays reachable,
    // it just costs nothing until someone asks for it.
    //
    // refreshCount();
    // fetchRecent();

    return () => {
      unsubscribe();
      client.disconnect();
      wsClientRef.current = null;
    };
  // sessionKey is the key dependency: changing it tears down the old WS and
  // opens a fresh one with the new session, seeding state from REST again.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, applicationId, refreshCount, fetchRecent, sessionKey]);

  return (
    <NotificationContext.Provider value={{
      unreadCount,
      recentNotifications,
      latestToast,
      markRead,
      markAllRead,
      refreshCount,
      fetchRecent,
    }}>
      {children}
    </NotificationContext.Provider>
  );
}

// ── Hook ──────────────────────────────────────────────────────────────────────
export function useNotifications() {
  return useContext(NotificationContext);
}

export default NotificationContext;
